<template>
  <div v-if="isShow" id="LoadingMap" :class="{[className]: true, mini: !message}">
    <span uk-spinner="ratio: 1"></span>
    <span class="message" v-if="message">{{ message }}</span>
  </div>
</template>

<script setup>
/**
 * ローディングの表現用
 * 
 * すぐ閉じてチラつくのを防ぐ役割が強い
 */
import { ref, watch, onBeforeUnmount } from 'vue'

const props = defineProps({
  /**
   * true: 表示の指示をだす
   * falseにしてもすぐ閉じないようにする
   */
  show: {
    type: Boolean,
    default: false
  },
  /**
   * 表示する文言
   */
  message: {
    type: String,
    default: ''
  },
  /**
   * 付与するクラス名
   */
  className: {
    type: String,
    default: ''
  },
  /**
   * 表示開始までの遅延時間[ms]
   * 
   * この時間以内にshowがfalseになった場合、表示しない
   */
  delay: {
    type: Number,
    default: 0
  }
})

/**
 * 表示開始から最低表示時間[ms]
 */
const MIN_DURATION = 500

/**
 * true: 表示
 */
const isShow = ref(false)

/**
 * 表示開始時刻
 * Date.now()
 */
let started = null

/**
 * 表示開始遅延のタイマーID
 */
let delayTimerId = null

/**
 * 最低表示時間のタイマーID
 */
let minDurationTimerId = null

/**
 * コンポーネント破棄時にタイマーをクリア
 */
onBeforeUnmount(() => {
  if (delayTimerId !== null) {
    clearTimeout(delayTimerId)
    delayTimerId = null
  }
  if (minDurationTimerId !== null) {
    clearTimeout(minDurationTimerId)
    minDurationTimerId = null
  }
})

/**
 * すべてのタイマーをクリア
 */
function clearAllTimers() {
  if (delayTimerId !== null) {
    clearTimeout(delayTimerId)
    delayTimerId = null
  }
  if (minDurationTimerId !== null) {
    clearTimeout(minDurationTimerId)
    minDurationTimerId = null
  }
}

/**
 * 表示を開始（遅延あり/なし）
 */
function startShow() {
  if (props.delay > 0) {
    // 遅延時間が設定されている場合、遅延後に表示開始
    delayTimerId = setTimeout(() => {
      isShow.value = true
      started = Date.now()
      delayTimerId = null
    }, props.delay)
  } else {
    // 遅延なしの場合は即座に表示
    isShow.value = true
    started = Date.now()
  }
}

/**
 * 表示を停止（最低表示時間を考慮）
 */
function stopShow() {
  // 遅延中の場合、これから出る分を取り消す
  // 表示中に再度showが立つと、出ているまま次の遅延タイマーを持つので、ここで抜けると消し損ねる
  if (delayTimerId !== null) {
    clearTimeout(delayTimerId)
    delayTimerId = null
  }

  // 表示していない場合は何もしない
  if (!isShow.value) {
    return
  }

  // 表示開始から一定時間後に閉じるようにする
  const diff = Date.now() - started

  if (diff < MIN_DURATION) {
    const wait = MIN_DURATION - diff
    minDurationTimerId = setTimeout(() => {
      isShow.value = false
      minDurationTimerId = null
    }, wait)
  } else {
    isShow.value = false
    started = null
  }
}

watch(() => props.show, (newValue) => {
  if (newValue) {
    clearAllTimers()
    startShow()
  } else {
    stopShow()
  }
}, { immediate: true })

</script>

<style lang="scss" scoped>
@keyframes fade-in-loading-map {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

#LoadingMap {
  animation: fade-in-loading-map .25s ease-out;
  position: absolute;
  max-width: 20em;
  height: 6em;
  bottom: calc(50% - 6em);
  margin: 0 auto;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border-radius: .25em;
  left: 50%;
  margin-right: -50%;
  transform: translate(-50%, -50%);
  text-align: center;
  font-size: 1rem;

  .uk-icon {
    margin: 1.25em 3em 0.75rem 3rem;
  }

  .message {
    margin: 0 3em 0 3rem;
    display: block;
  }

  &.selected {
    top: 64px;
    bottom: auto;
  }

  &.mini {
    height: 3em;
    bottom: calc(50% - 2em);
    .uk-spinner {
      margin: .5em 2em 0.5rem 2rem;
    }
  }
}
</style>